import React, {useCallback, useMemo} from 'react';
import type {SxProps, Theme} from '@mui/material';
import {FormControlLabel, Radio, RadioGroup} from '@mui/material';
import type {Code} from 'services/codelists/types';
import {UXUnit} from 'theme/settings';
import {
  RadiobuttonCheckedIcon,
  RadiobuttonUncheckedIcon,
} from 'components/icons';
import type {BaseFieldProps} from '../types';
import {Layout} from '../../layout';
import {FieldLabel} from '../FieldLabel';
import './CodeListField.scss';
import {getCodeLabel} from './functions';

type Props = BaseFieldProps & {
  name: string;
  id?: string;
  value?: string | null;
  options: Code[];
  showCode?: boolean;
  horizontal?: boolean;
  /**
   * Font variant to use for option labels, default label
   */
  variant?: 'button';
  onChange?: (value: string | null) => void;
  onDelete?: (value: string) => void;
  onBlur?: () => void;
};

export const RadioButtonsField: React.FC<Props> = ({
  name,
  id,
  value,
  showCode,
  required = false,
  error,
  label,
  options,
  horizontal,
  onChange,
  onBlur,
  layout,
  readonly,
}) => {
  const sxGroup = useMemo(
    (): SxProps<Theme> | undefined =>
      horizontal
        ? {
            flexDirection: 'row',
            transform: `translateX(${UXUnit}px)`,
          }
        : {
            alignItems: 'flex-start',
          },
    [horizontal],
  );

  const handleToggleValue = useCallback(
    (newValue: string) => {
      !readonly && onChange && onChange(newValue === value ? null : newValue);
    },
    [readonly, onChange, value],
  );

  return (
    <Layout {...layout}>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          error={error}
          htmlFor={name}
        />
      )}
      <RadioGroup
        aria-labelledby="demo-radio-buttons-group-label"
        name="radio-buttons-group"
        sx={sxGroup}
        value={value}
        onChange={(_, value) => handleToggleValue(value)}
        onBlur={onBlur}>
        {options.map(opt => (
          <FormControlLabel
            key={opt.code}
            value={opt.code}
            htmlFor={`${id}:${opt.code}`}
            onClick={() => handleToggleValue(opt.code)}
            disabled={readonly}
            control={
              <Radio
                icon={
                  error === true ? (
                    <RadiobuttonUncheckedIcon color="error" />
                  ) : error === 'warning' ? (
                    <RadiobuttonUncheckedIcon color="warning" />
                  ) : (
                    <RadiobuttonUncheckedIcon />
                  )
                }
                checkedIcon={<RadiobuttonCheckedIcon />}
                onKeyUp={e => {
                  if (e.code === 'Space') {
                    handleToggleValue(opt.code);
                    e.preventDefault();
                  }
                }}
              />
            }
            color={
              error === true
                ? 'error'
                : error === 'warning'
                  ? 'warning'
                  : undefined
            }
            label={getCodeLabel(opt, showCode)}
          />
        ))}
      </RadioGroup>
    </Layout>
  );
};
